// File: frontend/src/pages/BlogPage.js
// Version: 1.0 (2026-10-22, Task 3)
// Description: Раздел «Блог» в Fitenko OS.
// Вкладки:
//  1. Вышло (Published): вышедшие статьи, фильтр 7/30/все дни, прямые ссылки EN и RU, статус проверки
//  2. В очереди (In Queue): ожидающие публикации статьи, прогноз по датам
//  3. Темы (Topic Suggestions): рекомендованные темы, фильтр по статусу, форма добавления

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    AlertTriangle,
    CheckCircle2,
    Clock,
    ExternalLink,
    Plus,
    XCircle,
} from 'lucide-react';
import apiService from '../services/apiService';
import { useUiLanguage } from '../context/UiLanguageContext';
import {
    Badge,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    EmptyState,
    Input,
    PageHeader,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
    Textarea,
} from '../components/ui';

export default function BlogPage() {
    const { t, lang } = useUiLanguage();

    const [activeTab, setActiveTab] = useState('published');
    const [overviewData, setOverviewData] = useState(null);
    const [loadingOverview, setLoadingOverview] = useState(true);

    // Filter for published tab: '7d' | '30d' | 'all'
    const [publishedFilter, setPublishedFilter] = useState('7d');

    // Suggestions state
    const [suggestions, setSuggestions] = useState([]);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const [suggestionStatusFilter, setSuggestionStatusFilter] = useState('all');

    // Modal state for proposing a topic
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [submittingModal, setSubmittingModal] = useState(false);
    const [modalError, setModalError] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        language: 'both',
        keyword: '',
        priority: 'normal',
        notes: '',
        queue_id: '',
    });

    // Load overview data
    const loadOverview = useCallback(async () => {
        try {
            setLoadingOverview(true);
            const data = await apiService.getBlogOverview();
            setOverviewData(data);
        } catch (err) {
            console.error('Error loading blog overview', err);
        } finally {
            setLoadingOverview(false);
        }
    }, []);

    // Load suggestions
    const loadSuggestions = useCallback(async () => {
        try {
            setLoadingSuggestions(true);
            const items = await apiService.getBlogSuggestions(suggestionStatusFilter);
            setSuggestions(Array.isArray(items) ? items : []);
        } catch (err) {
            console.error('Failed to load blog suggestions', err);
        } finally {
            setLoadingSuggestions(false);
        }
    }, [suggestionStatusFilter]);

    useEffect(() => {
        loadOverview();
    }, [loadOverview]);

    useEffect(() => {
        if (activeTab === 'suggestions') {
            loadSuggestions();
        }
    }, [activeTab, loadSuggestions]);

    const snapshot = overviewData?.snapshot || null;
    const isStale = !!overviewData?.stale;
    const receivedAt = overviewData?.received_at || null;

    // Filter published articles
    const publishedArticles = useMemo(() => {
        const list = snapshot?.published || [];
        if (publishedFilter === 'all') return list;

        const now = new Date();
        const daysLimit = publishedFilter === '7d' ? 7 : 30;
        const cutoff = new Date(now.getTime() - daysLimit * 24 * 60 * 60 * 1000);

        return list.filter((item) => {
            if (!item.date) return true;
            const itemDate = new Date(item.date);
            return isNaN(itemDate.getTime()) ? true : itemDate >= cutoff;
        });
    }, [snapshot?.published, publishedFilter]);

    // Count for 7 days
    const count7d = useMemo(() => {
        const list = snapshot?.published || [];
        const now = new Date();
        const cutoff7d = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        return list.filter((item) => {
            if (!item.date) return false;
            const itemDate = new Date(item.date);
            return !isNaN(itemDate.getTime()) && itemDate >= cutoff7d;
        }).length;
    }, [snapshot?.published]);

    // Pending articles
    const pendingArticles = useMemo(() => {
        return snapshot?.pending || [];
    }, [snapshot?.pending]);

    // Latest expected date for pending banner
    const lastExpectedDate = useMemo(() => {
        if (!pendingArticles.length) return null;
        const sorted = [...pendingArticles].sort((a, b) => (a.expected_date || '').localeCompare(b.expected_date || ''));
        return sorted[sorted.length - 1]?.expected_date || null;
    }, [pendingArticles]);

    // Suggestion actions
    const handleStatusChange = async (id, newStatus) => {
        try {
            await apiService.updateBlogSuggestion(id, { status: newStatus });
            loadSuggestions();
        } catch (err) {
            console.error('Failed to update topic status', err);
        }
    };

    // Modal submit
    const handleSubmitSuggestion = async (e) => {
        if (e) e.preventDefault();
        if (!formData.title.trim()) return;

        try {
            setSubmittingModal(true);
            setModalError(null);
            await apiService.createBlogSuggestion({
                title: formData.title.trim(),
                language: formData.language,
                keyword: formData.keyword.trim() || undefined,
                priority: formData.priority,
                notes: formData.notes.trim() || undefined,
                queue_id: formData.queue_id.trim() || undefined,
            });
            setIsModalOpen(false);
            setFormData({
                title: '',
                language: 'both',
                keyword: '',
                priority: 'normal',
                notes: '',
                queue_id: '',
            });
            loadSuggestions();
        } catch (err) {
            setModalError(err.message || t('blog.modal.error'));
        } finally {
            setSubmittingModal(false);
        }
    };

    return (
        <div className="space-y-6 p-6 max-w-7xl mx-auto" data-testid="blog-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-border pb-4">
                <div>
                    <PageHeader
                        title={t('blog.header.title')}
                        description={t('blog.header.subtitle')}
                    />
                    {receivedAt && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                            <Clock className="w-3.5 h-3.5" />
                            <span>{t('blog.status.pipeline_date', { date: receivedAt })}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Stale Banner */}
            {isStale && receivedAt && (
                <div
                    data-testid="stale-banner"
                    style={{
                        backgroundColor: 'var(--warning-soft)',
                        borderColor: 'var(--warning-border)',
                        color: 'var(--text)',
                    }}
                    className="p-4 rounded-lg border flex items-center gap-3 text-sm"
                >
                    <AlertTriangle className="w-5 h-5 shrink-0" style={{ color: 'var(--warning)' }} />
                    <span>{t('blog.banner.stale', { time: receivedAt })}</span>
                </div>
            )}

            {/* No Data State */}
            {!loadingOverview && snapshot === null ? (
                <div data-testid="no-data-banner">
                    <EmptyState
                        icon={<Clock className="w-6 h-6" />}
                        title={t('blog.banner.no_data')}
                        description=""
                    />
                </div>
            ) : (
                /* Tabs */
                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
                    <TabsList>
                        <TabsTrigger value="published" data-testid="tab-trigger-published">
                            {t('blog.tabs.published')} ({snapshot?.published?.length || 0})
                        </TabsTrigger>
                        <TabsTrigger value="pending" data-testid="tab-trigger-pending">
                            {t('blog.tabs.pending')} ({snapshot?.pending?.length || 0})
                        </TabsTrigger>
                        <TabsTrigger value="suggestions" data-testid="tab-trigger-suggestions">
                            {t('blog.tabs.suggestions')}
                        </TabsTrigger>
                    </TabsList>

                    {/* Tab 1: Published */}
                    <TabsContent value="published" className="space-y-4">
                        {/* Filters & Counters */}
                        <div className="flex flex-wrap items-center justify-between gap-4">
                            <div className="flex items-center gap-2">
                                <Button
                                    size="sm"
                                    variant={publishedFilter === '7d' ? 'default' : 'outline'}
                                    onClick={() => setPublishedFilter('7d')}
                                >
                                    {t('blog.published.filter_7d')}
                                </Button>
                                <Button
                                    size="sm"
                                    variant={publishedFilter === '30d' ? 'default' : 'outline'}
                                    onClick={() => setPublishedFilter('30d')}
                                >
                                    {t('blog.published.filter_30d')}
                                </Button>
                                <Button
                                    size="sm"
                                    variant={publishedFilter === 'all' ? 'default' : 'outline'}
                                    onClick={() => setPublishedFilter('all')}
                                >
                                    {t('blog.published.filter_all')}
                                </Button>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-muted-foreground">
                                <span>{t('blog.published.count_7d', { n: count7d })}</span>
                                <span>•</span>
                                <span>{t('blog.published.count_total', { n: snapshot?.published?.length || 0 })}</span>
                            </div>
                        </div>

                        {/* Published Table */}
                        <Card>
                            <CardContent className="p-0">
                                {publishedArticles.length === 0 ? (
                                    <div className="p-8 text-center text-muted-foreground text-sm">
                                        {t('blog.published.empty')}
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="w-32">{t('blog.published.col_date')}</TableHead>
                                                <TableHead>{t('blog.published.col_title')}</TableHead>
                                                <TableHead className="w-40">{t('blog.published.col_category')}</TableHead>
                                                <TableHead className="w-32">{t('blog.published.col_links')}</TableHead>
                                                <TableHead className="w-32">{t('blog.published.col_verify')}</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {publishedArticles.map((item, idx) => {
                                                const title = lang === 'ru' ? (item.title_ru || item.title) : (item.title || item.title_ru);
                                                const category = lang === 'ru' ? (item.category_ru || item.category) : (item.category || item.category_ru);
                                                return (
                                                    <TableRow key={item.queue_id || idx} data-testid="published-row">
                                                        <TableCell className="font-mono text-xs whitespace-nowrap">
                                                            {item.date}
                                                        </TableCell>
                                                        <TableCell className="font-medium text-foreground">
                                                            {title}
                                                        </TableCell>
                                                        <TableCell className="text-muted-foreground text-xs">
                                                            {category}
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex items-center gap-2">
                                                                {item.url_en && (
                                                                    <a
                                                                        href={item.url_en}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        data-testid="link-en"
                                                                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline font-medium"
                                                                    >
                                                                        {t('blog.link.en')}
                                                                        <ExternalLink className="w-3 h-3" />
                                                                    </a>
                                                                )}
                                                                {item.url_ru && (
                                                                    <a
                                                                        href={item.url_ru}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        data-testid="link-ru"
                                                                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline font-medium"
                                                                    >
                                                                        {t('blog.link.ru')}
                                                                        <ExternalLink className="w-3 h-3" />
                                                                    </a>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            {item.verify === 'ok' ? (
                                                                <Badge variant="success">
                                                                    <CheckCircle2 className="w-3 h-3 mr-1 inline" />
                                                                    {t('blog.verify.ok')}
                                                                </Badge>
                                                            ) : item.verify === 'fail' ? (
                                                                <Badge variant="destructive">
                                                                    <XCircle className="w-3 h-3 mr-1 inline" />
                                                                    {t('blog.verify.fail')}
                                                                </Badge>
                                                            ) : (
                                                                <Badge variant="secondary">
                                                                    {t('blog.verify.unverified')}
                                                                </Badge>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Tab 2: In Queue */}
                    <TabsContent value="pending" className="space-y-4">
                        {/* Queue Banner */}
                        <div
                            data-testid="pending-banner"
                            className="p-4 rounded-lg bg-muted border border-border text-foreground text-sm font-medium flex items-center justify-between"
                        >
                            <span>
                                {pendingArticles.length > 0 && lastExpectedDate
                                    ? t('blog.pending.banner', { n: pendingArticles.length, date: lastExpectedDate })
                                    : t('blog.pending.banner_empty')}
                            </span>
                        </div>

                        {/* Pending Table */}
                        <Card>
                            <CardContent className="p-0">
                                {pendingArticles.length === 0 ? (
                                    <div className="p-8 text-center text-muted-foreground text-sm">
                                        {t('blog.pending.empty')}
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="w-16">{t('blog.pending.col_pos')}</TableHead>
                                                <TableHead className="w-40">{t('blog.pending.col_expected')}</TableHead>
                                                <TableHead>{t('blog.pending.col_title')}</TableHead>
                                                <TableHead className="w-40">{t('blog.pending.col_category')}</TableHead>
                                                <TableHead className="w-48">{t('blog.pending.col_queue_id')}</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {pendingArticles.map((item, idx) => {
                                                const title = lang === 'ru' ? (item.title_ru || item.title) : (item.title || item.title_ru);
                                                return (
                                                    <TableRow key={item.queue_id || idx} data-testid="pending-row">
                                                        <TableCell className="font-mono text-xs text-muted-foreground">
                                                            {idx + 1}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge variant="info" className="whitespace-nowrap">
                                                                <Clock className="w-3 h-3 mr-1 inline" />
                                                                {t('blog.pending.expected_label', { date: item.expected_date })}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell className="font-medium text-foreground">
                                                            {title}
                                                        </TableCell>
                                                        <TableCell className="text-muted-foreground text-xs">
                                                            {item.category}
                                                        </TableCell>
                                                        <TableCell className="font-mono text-xs text-muted-foreground">
                                                            {item.queue_id}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Tab 3: Topic Suggestions */}
                    <TabsContent value="suggestions" className="space-y-4">
                        {/* Top bar: Propose Button + Filter */}
                        <div className="flex flex-wrap items-center justify-between gap-4">
                            <Button
                                onClick={() => setIsModalOpen(true)}
                                data-testid="btn-propose-topic"
                                className="inline-flex items-center gap-2"
                            >
                                <Plus className="w-4 h-4" />
                                {t('blog.suggestions.btn_propose')}
                            </Button>

                            <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">{t('blog.suggestions.filter_all')}:</span>
                                <select
                                    value={suggestionStatusFilter}
                                    onChange={(e) => setSuggestionStatusFilter(e.target.value)}
                                    className="h-9 px-3 rounded-md border border-input bg-background text-sm"
                                    data-testid="select-suggestion-filter"
                                >
                                    <option value="all">{t('blog.suggestions.filter_all')}</option>
                                    <option value="proposed">{t('blog.suggestions.filter_proposed')}</option>
                                    <option value="accepted">{t('blog.suggestions.filter_accepted')}</option>
                                    <option value="in_queue">{t('blog.suggestions.filter_in_queue')}</option>
                                    <option value="published">{t('blog.suggestions.filter_published')}</option>
                                    <option value="rejected">{t('blog.suggestions.filter_rejected')}</option>
                                </select>
                            </div>
                        </div>

                        {/* Suggestions Table */}
                        <Card>
                            <CardContent className="p-0">
                                {loadingSuggestions ? (
                                    <div className="p-8 text-center text-muted-foreground text-sm">
                                        ...
                                    </div>
                                ) : suggestions.length === 0 ? (
                                    <div className="p-8 text-center text-muted-foreground text-sm">
                                        {t('blog.suggestions.empty')}
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>{t('blog.suggestions.col_title')}</TableHead>
                                                <TableHead className="w-36">{t('blog.suggestions.col_keyword')}</TableHead>
                                                <TableHead className="w-24">{t('blog.suggestions.col_priority')}</TableHead>
                                                <TableHead className="w-24">{t('blog.suggestions.col_language')}</TableHead>
                                                <TableHead className="w-32">{t('blog.suggestions.col_status')}</TableHead>
                                                <TableHead className="w-36">{t('blog.suggestions.col_queue_id')}</TableHead>
                                                <TableHead className="w-36 text-right">{t('blog.suggestions.col_actions')}</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {suggestions.map((s) => (
                                                <TableRow key={s.id} data-testid="suggestion-row">
                                                    <TableCell>
                                                        <div className="font-medium text-foreground">{s.title}</div>
                                                        {s.notes && (
                                                            <div className="text-xs text-muted-foreground mt-0.5">{s.notes}</div>
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-xs text-muted-foreground">
                                                        {s.keyword || '—'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {s.priority === 'high' ? (
                                                            <Badge variant="destructive">{t('blog.priority.high')}</Badge>
                                                        ) : s.priority === 'low' ? (
                                                            <Badge variant="outline">{t('blog.priority.low')}</Badge>
                                                        ) : (
                                                            <Badge variant="secondary">{t('blog.priority.normal')}</Badge>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant="outline">
                                                            {s.language === 'both'
                                                                ? t('blog.language.both')
                                                                : s.language === 'ru'
                                                                ? t('blog.language.ru')
                                                                : t('blog.language.en')}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge
                                                            variant={
                                                                s.status === 'published'
                                                                    ? 'default'
                                                                    : s.status === 'in_queue'
                                                                    ? 'secondary'
                                                                    : s.status === 'rejected'
                                                                    ? 'outline'
                                                                    : 'outline'
                                                            }
                                                        >
                                                            {s.status === 'published'
                                                                ? t('blog.status.published')
                                                                : s.status === 'in_queue'
                                                                ? t('blog.status.in_queue')
                                                                : s.status === 'accepted'
                                                                ? t('blog.status.accepted')
                                                                : s.status === 'rejected'
                                                                ? t('blog.status.rejected')
                                                                : t('blog.status.proposed')}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="font-mono text-xs text-muted-foreground">
                                                        {s.queue_id || '—'}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <div className="flex items-center justify-end gap-1">
                                                            {s.status === 'proposed' && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    className="h-7 px-2"
                                                                    style={{ color: 'var(--success)' }}
                                                                    onClick={() => handleStatusChange(s.id, 'accepted')}
                                                                >
                                                                    {t('blog.action.accept')}
                                                                </Button>
                                                            )}
                                                            {s.status !== 'rejected' && s.status !== 'published' && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    className="h-7 px-2"
                                                                    style={{ color: 'var(--danger)' }}
                                                                    onClick={() => handleStatusChange(s.id, 'rejected')}
                                                                >
                                                                    {t('blog.action.reject')}
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            )}

            {/* Modal: Suggest Topic */}
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('blog.modal.title')}</DialogTitle>
                    </DialogHeader>

                    <form onSubmit={handleSubmitSuggestion} className="space-y-4 py-2" data-testid="form-suggest-topic">
                        {modalError && (
                            <div className="p-3 text-xs rounded bg-destructive/10 text-destructive">
                                {modalError}
                            </div>
                        )}

                        <div className="space-y-1">
                            <label className="text-xs font-medium text-foreground">
                                {t('blog.modal.field_title')}
                            </label>
                            <Input
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                placeholder="e.g. EB-1A Extraordinary Ability Filing Checklist"
                                required
                                data-testid="input-modal-title"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <label className="text-xs font-medium text-foreground">
                                    {t('blog.modal.field_keyword')}
                                </label>
                                <Input
                                    value={formData.keyword}
                                    onChange={(e) => setFormData({ ...formData, keyword: e.target.value })}
                                    placeholder="e.g. EB-1A checklist"
                                    data-testid="input-modal-keyword"
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs font-medium text-foreground">
                                    {t('blog.modal.field_language')}
                                </label>
                                <select
                                    value={formData.language}
                                    onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                                    className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                                    data-testid="select-modal-language"
                                >
                                    <option value="both">{t('blog.language.both')}</option>
                                    <option value="en">{t('blog.language.en')}</option>
                                    <option value="ru">{t('blog.language.ru')}</option>
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <label className="text-xs font-medium text-foreground">
                                    {t('blog.modal.field_priority')}
                                </label>
                                <select
                                    value={formData.priority}
                                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                                    className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                                    data-testid="select-modal-priority"
                                >
                                    <option value="high">{t('blog.priority.high')}</option>
                                    <option value="normal">{t('blog.priority.normal')}</option>
                                    <option value="low">{t('blog.priority.low')}</option>
                                </select>
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs font-medium text-foreground">
                                    {t('blog.modal.field_queue_id')}
                                </label>
                                <Input
                                    value={formData.queue_id}
                                    onChange={(e) => setFormData({ ...formData, queue_id: e.target.value })}
                                    placeholder="e.g. 59-topic-slug"
                                    data-testid="input-modal-queue-id"
                                />
                            </div>
                        </div>

                        <div className="space-y-1">
                            <label className="text-xs font-medium text-foreground">
                                {t('blog.modal.field_notes')}
                            </label>
                            <Textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                placeholder="Additional details or angle for the writer..."
                                rows={3}
                                data-testid="input-modal-notes"
                            />
                        </div>

                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setIsModalOpen(false)}
                                disabled={submittingModal}
                            >
                                {t('blog.modal.btn_cancel')}
                            </Button>
                            <Button
                                type="submit"
                                disabled={submittingModal || !formData.title.trim()}
                                data-testid="btn-modal-submit"
                            >
                                {submittingModal ? t('blog.modal.submitting') : t('blog.modal.btn_submit')}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
